// One business on the Trade tab: where it's at, what to do next, the history
// of visits/calls/samples, and its invoices.
import { useEffect, useState } from 'react';
import { api } from '../lib/auth';
import { Button, Card, Input, Textarea } from '../lib/ui';
import {
  STAGES, STAGE, ACTIVITY_TYPES, ACTIVITY_LABEL, OPEN_STAGES, accountTotals, addDays, formatDay, formatGBP, formatPounds, ymd,
} from '../lib/trade';
import { FollowUpTag, InvoicePill, Label, errorText } from './TradeCommon';

// Entries logged "now" carry a time; back-dated ones are a bare day (UTC midnight).
function entryDay(date) {
  const s = String(date || '');
  return s.endsWith('T00:00:00.000Z')
    ? formatDay(s)
    : new Date(s).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function entryTitle(a) {
  if (a.type === 'stage_change') {
    return a.fromStage
      ? `Moved to ${STAGE[a.toStage]?.label || a.toStage}`
      : `Added as ${STAGE[a.toStage]?.label || a.toStage}`;
  }
  return ACTIVITY_LABEL[a.type] || a.type;
}

const MANUAL = new Set(ACTIVITY_TYPES.map(t => t.key));

function NextStep({ account, today, onSave, onDone }) {
  const [editing, setEditing] = useState(!account.nextStep);
  const [text, setText] = useState(account.nextStep || '');
  const [date, setDate] = useState(ymd(account.nextStepDate));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setEditing(!account.nextStep);
    setText(account.nextStep || '');
    setDate(ymd(account.nextStepDate));
  }, [account._id, account.nextStep, account.nextStepDate]);

  const save = async () => {
    setSaving(true);
    try { await onSave({ nextStep: text.trim(), nextStepDate: text.trim() ? date || null : null }); }
    finally { setSaving(false); }
  };

  if (!editing) {
    return (
      <Card>
        <h3>Next step</h3>
        <div className="trade-row" style={{ alignItems: 'baseline', marginBottom: 12 }}>
          <FollowUpTag account={account} today={today} />
          <span style={{ fontSize: 14, fontWeight: 600 }}>{account.nextStep}</span>
          {account.nextStepDate && <span className="muted" style={{ fontSize: 12 }}>{formatDay(account.nextStepDate, { weekday: true })}</span>}
        </div>
        <div className="trade-row">
          <Button variant="primary" size="sm" onClick={onDone}>Done</Button>
          <Button size="sm" onClick={() => setEditing(true)}>Change</Button>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <h3>Next step</h3>
      <div className="trade-row" style={{ alignItems: 'flex-start' }}>
        <Input
          style={{ flex: '1 1 220px' }}
          placeholder="e.g. Drop samples in, call the chef back"
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && save()}
        />
        <Input type="date" style={{ width: 150 }} value={date} onChange={e => setDate(e.target.value)} />
      </div>
      <div className="trade-quick">
        {[['Tomorrow', 1], ['In 3 days', 3], ['Next week', 7], ['In 2 weeks', 14]].map(([label, n]) => (
          <button key={label} type="button" onClick={() => setDate(addDays(today, n))}>{label}</button>
        ))}
      </div>
      <div className="trade-row" style={{ marginTop: 12 }}>
        <Button variant="primary" size="sm" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
        {account.nextStep && <Button size="sm" variant="muted" onClick={() => setEditing(false)}>Cancel</Button>}
      </div>
    </Card>
  );
}

function LogActivity({ today, onLog }) {
  const [type, setType] = useState('visit');
  const [date, setDate] = useState(today);
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);

  const log = async () => {
    setSaving(true);
    try {
      await onLog({ type, text: text.trim(), date: date === today ? undefined : date });
      setText('');
      setDate(today);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <h3>Log what happened</h3>
      <div className="trade-seg" style={{ marginBottom: 10 }}>
        {ACTIVITY_TYPES.map(t => (
          <button key={t.key} type="button" className={type === t.key ? 'is-on' : ''} onClick={() => setType(t.key)}>{t.label}</button>
        ))}
      </div>
      <Textarea
        rows={3}
        placeholder={type === 'sample' ? 'What did you leave them?' : 'Who did you speak to, what did they say?'}
        value={text}
        onChange={e => setText(e.target.value)}
      />
      <div className="trade-row" style={{ marginTop: 10 }}>
        <Input type="date" style={{ width: 150 }} value={date} max={today} onChange={e => setDate(e.target.value || today)} />
        <Button variant="primary" onClick={log} disabled={saving || (type === 'note' && !text.trim())}>
          {saving ? 'Saving…' : 'Log it'}
        </Button>
      </div>
    </Card>
  );
}

export default function TradeAccountView({ account, invoices, trade, today, flash, onBack, onEdit, onNewInvoice, onOpenInvoice }) {
  const totals = accountTotals(invoices, today)[account._id];
  const terms = account.termsDays ?? trade.settings?.termsDays;
  const address = [account.address?.line1, account.address?.line2, account.address?.city, account.address?.postcode].filter(Boolean).join(', ');

  const patch = async (fields, done) => {
    try {
      const { data } = await api.patch(`/trade/accounts/${account._id}`, fields);
      trade.putAccount(data);
      if (done) flash(done);
    } catch (e) {
      flash(errorText(e), 'bad');
      throw e;
    }
  };

  const setStage = (stage) => stage !== account.stage && patch({ stage }, `Moved to ${STAGE[stage].label}`).catch(() => {});

  const logActivity = async (body, done = 'Logged') => {
    try {
      const { data } = await api.post(`/trade/accounts/${account._id}/activity`, body);
      trade.putAccount(data);
      flash(done);
    } catch (e) {
      flash(errorText(e), 'bad');
    }
  };

  const removeEntry = async (entry) => {
    if (!window.confirm(`Remove this ${entryTitle(entry).toLowerCase()} from the history?`)) return;
    try {
      const { data } = await api.delete(`/trade/accounts/${account._id}/activity/${entry._id}`);
      trade.putAccount(data);
    } catch (e) {
      flash(errorText(e), 'bad');
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete ${account.businessName} and its history? This can't be undone.`)) return;
    try {
      await api.delete(`/trade/accounts/${account._id}`);
      trade.dropAccount(account._id);
      flash(`${account.businessName} deleted`);
      onBack();
    } catch (e) {
      flash(errorText(e), 'bad');
    }
  };

  const history = [...(account.activity || [])].sort((a, b) =>
    String(b.date).localeCompare(String(a.date)) || String(b.createdAt).localeCompare(String(a.createdAt)));
  const sortedInvoices = [...invoices].sort((a, b) => ymd(b.issueDate).localeCompare(ymd(a.issueDate)) || b.seq - a.seq);

  return (
    <div>
      <Button variant="muted" className="trade-back" onClick={onBack}>← All businesses</Button>

      <Card className="trade-hero">
        <div className="trade-hero__top">
          <div style={{ minWidth: 0 }}>
            <h2>{account.businessName}</h2>
            <div className="trade-hero__contact">
              {account.contactName && <span>{account.contactName}</span>}
              {account.phone && <a href={`tel:${account.phone.replace(/\s+/g, '')}`}>{account.phone}</a>}
              {account.email && <a href={`mailto:${account.email}`}>{account.email}</a>}
              {address && <span>{address}</span>}
            </div>
          </div>
          <div className="trade-row">
            <Button onClick={onEdit}>Edit details</Button>
            {!invoices.length && <Button variant="danger" onClick={remove}>Delete</Button>}
          </div>
        </div>

        <div className="trade-hero__facts">
          {OPEN_STAGES.includes(account.stage) && account.potentialWeekly > 0 && (
            <div><span>Potential</span>{formatPounds(account.potentialWeekly)} a week</div>
          )}
          {account.interestedIn && <div style={{ maxWidth: 360 }}><span>Interested in</span>{account.interestedIn}</div>}
          {totals && <div><span>Invoiced</span>{formatGBP(totals.invoiced)} over {totals.count} invoice{totals.count === 1 ? '' : 's'}</div>}
          {totals?.unpaid > 0 && (
            <div style={{ color: totals.overdue ? 'var(--bad-fg)' : undefined }}>
              <span>Unpaid</span>{formatGBP(totals.unpaid)}{totals.overdue ? `, ${formatGBP(totals.overdue)} overdue` : ''}
            </div>
          )}
          {terms != null && <div><span>Pays</span>{terms ? `${terms} days after invoice` : 'On receipt'}</div>}
        </div>

        <div className="trade-hero__stage">
          <Label>Stage</Label>
          <div className="trade-seg">
            {STAGES.map(s => (
              <button key={s.key} type="button" title={s.hint} className={account.stage === s.key ? 'is-on' : ''} onClick={() => setStage(s.key)}>
                {s.label}
              </button>
            ))}
          </div>
        </div>
      </Card>

      <div className="trade-detail">
        <div className="trade-detail__col">
          <NextStep
            account={account}
            today={today}
            onSave={(fields) => patch(fields, fields.nextStep ? 'Next step saved' : 'Next step cleared')}
            onDone={() => logActivity({ type: 'note', text: `Done: ${account.nextStep}`, clearNextStep: true }, 'Done. What\'s next?')}
          />
          <LogActivity today={today} onLog={(body) => logActivity(body)} />
          <Card>
            <h3>History</h3>
            {history.length === 0 ? <p className="muted">Nothing logged yet.</p> : (
              <ul className="trade-timeline">
                {history.map(a => (
                  <li key={a._id} className={`is-${a.type}`}>
                    <div className="trade-timeline__head">
                      <b>{entryTitle(a)}</b>
                      <time>{entryDay(a.date)}</time>
                      {MANUAL.has(a.type) && <button type="button" title="Remove" onClick={() => removeEntry(a)}>×</button>}
                    </div>
                    {a.text && <p>{a.text}</p>}
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>

        <div className="trade-detail__col">
          <Card>
            <div className="trade-row" style={{ justifyContent: 'space-between', marginBottom: 12 }}>
              <h3 style={{ margin: 0 }}>Invoices</h3>
              <Button variant="primary" size="sm" onClick={onNewInvoice}>+ New invoice</Button>
            </div>
            {sortedInvoices.length === 0 ? (
              <p className="muted">No invoices yet. Their first invoice moves them to Customer.</p>
            ) : (
              <ul className="trade-mini-list">
                {sortedInvoices.map(inv => (
                  <li key={inv._id} onClick={() => onOpenInvoice(inv._id)}>
                    <b>{inv.number}</b>
                    <span className="muted">{formatDay(inv.issueDate)}</span>
                    <InvoicePill invoice={inv} today={today} />
                    <span className="amt">{formatGBP(inv.total)}</span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
